import { AuthApi, LoginDTO, ResetPasswordDTO, TokenResponseDTO } from '@reposit/api-client';
import { AxiosResponse } from 'axios';
import { push } from 'connected-react-router';
import { call, delay, put, takeLatest } from 'redux-saga/effects';
import { FlashState } from '../../components/FlashMessage';
import { getErrorMessage } from '../../utils/common.utils';
import { setHasRequestedPasswordReset } from '../account/account.actions';
import { setFlashMessage } from '../flash-messages/flash-messages.actions';
import { createAuthApi, runSagaWithAuth } from '../utils/api.utils';
import {
  LOGOUT_SESSION_STORE_KEY,
  loginFailed,
  loginRequested,
  loginSuccess,
  logout,
  logoutSessionFailed,
  logoutSessionRequested,
  logoutSessionSuccess,
  resetPasswordFailed,
  resetPasswordRequested,
  resetPasswordRequestFailed,
  resetPasswordRequestRequested,
  resetPasswordRequestSuccess,
  resetPasswordSuccess,
  setPasswordFailed,
  setPasswordRequested,
  setPasswordSuccess,
  setToken,
} from './auth.actions';
import { ResetPasswordPayload } from './auth.types';
import { internalSessionRequestOptions } from './internal-session';

export function* login({ payload }: { payload: LoginDTO; type: string }) {
  try {
    const authApi: AuthApi = yield createAuthApi();
    // With credentials, so the browser keeps the session cookie tenancy sets for admins.
    const apiResponse: AxiosResponse<TokenResponseDTO> = yield call(
      runSagaWithAuth(() => authApi.login(payload, { withCredentials: true })),
    );
    if (apiResponse.data.requiresReset) {
      yield put(setHasRequestedPasswordReset(true));
    } else if (apiResponse.data.accessToken) {
      yield put(setToken(apiResponse.data.accessToken));
      yield put(loginSuccess());
    }
    // might need to be smarter
    yield push('/reposits');
  } catch (e) {
    const error = getErrorMessage(e);
    yield put(loginFailed(error));
  }
}

export function* resetPasswordRequest({ payload }: { payload: string; type: string }) {
  try {
    const authApi: AuthApi = yield createAuthApi();
    yield call(runSagaWithAuth(() => authApi.resetPasswordRequest(payload)));
    yield put(resetPasswordRequestSuccess());
    yield put(setHasRequestedPasswordReset(true));
  } catch (e) {
    const error = getErrorMessage(e);
    yield put(resetPasswordRequestFailed(error));
  }
}

export function* resetPassword({ payload }: { payload: ResetPasswordPayload; type: string }) {
  try {
    const authApi: AuthApi = yield createAuthApi();
    yield call(runSagaWithAuth(() => authApi.resetPassword({ token: payload.token, password: payload.password })));
    yield put(resetPasswordSuccess(payload.email));
    yield delay(4000);
    yield put(push('/auth/login'));
  } catch (e) {
    const error = getErrorMessage(e);
    yield put(resetPasswordFailed(error));
  }
}

export function* setPassword({ payload }: { payload: ResetPasswordDTO; type: string }) {
  try {
    const authApi: AuthApi = yield createAuthApi();
    const apiResponse: AxiosResponse<TokenResponseDTO> = yield call(
      runSagaWithAuth(() => authApi.setPassword(payload, { withCredentials: true })),
    );
    if (apiResponse.data.accessToken) {
      yield put(setToken(apiResponse.data.accessToken));
      yield put(setPasswordSuccess());
      // might need to be smarter
      yield push('/reposits');
    }
  } catch (e) {
    const error = getErrorMessage(e);
    yield put(setPasswordFailed(error));
  }
}

// Signs out only once tenancy has ended the session. Clearing the token first would leave the cookie behind,
// and the next reload would restore the session.
export function* logoutSession() {
  try {
    const authApi: AuthApi = yield createAuthApi();
    yield call(() => authApi.logoutInternalSession(internalSessionRequestOptions));
    yield put(logoutSessionSuccess());
    yield put(logout());
    yield put(push('/auth/login'));
  } catch (e) {
    yield put(logoutSessionFailed(getErrorMessage(e)));
    yield put(
      setFlashMessage({
        key: LOGOUT_SESSION_STORE_KEY,
        message: 'We could not log you out. Please try again.',
        state: FlashState.ERROR,
      }),
    );
  }
}

// ****************
// WATCHERS
// ****************
export function* watchAuthSagas() {
  yield takeLatest(loginRequested.type, login);
  yield takeLatest(resetPasswordRequestRequested.type, resetPasswordRequest);
  yield takeLatest(resetPasswordRequested.type, resetPassword);
  yield takeLatest(setPasswordRequested.type, setPassword);
  yield takeLatest(logoutSessionRequested.type, logoutSession);
}
